import {
  useRevenueByTypeReport,
  useRevenueByTypeReportCsv,
} from "@gymflow/api";
import {
  DATE_FORMAT,
  DATE_FORMAT_LUXON,
  PARAMETER_DATE_FORMAT_WITHOUT_TZ,
  RevenueType,
} from "@gymflow/common";
import { downloadCsv, formatCurrency } from "@gymflow/helpers";
import { TransactionReportItemDTO } from "@gymflow/types";
import { createColumnHelper, SortingState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import moment from "moment-timezone";
import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { useHistory, useLocation } from "react-router";

import { ReportPagination } from "../../components/molecules";
import { Table } from "../../components/organisms";
import { Report } from "../../components/organisms/Report";
import useQueryParam from "../../hooks/useQueryParam";
import { useClubSettings } from "../../providers";
import useGymflowModels from "../../store";
import { formatDateRange, getDefaultDateRange } from "./helpers/dateHelpers";

export function RevenueAccountsReceivable() {
  const history = useHistory();
  const { api } = useGymflowModels();

  const location = useLocation();
  const searchParams = qs.parse(location.search, { ignoreQueryPrefix: true });

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState(25);
  const [currentSort, setCurrentSort] = useState<SortingState>([]);

  const {
     featureFlags, club, properties ,
  } = useClubSettings();

  const [dates, setDates] = useQueryParam("dates", "string") as any;
  const defaultDates = useMemo(
    () => ({
      startDate: moment().startOf("month").format(DATE_FORMAT),
      endDate: moment().format(DATE_FORMAT),
    }),
    [],
  );

  const defaultRange = useMemo(
    () => getDefaultDateRange(club.timezone),
    [club.timezone],
  );

  useEffect(() => {
    if (featureFlags.PORTAL_NEW_REPORTS_DATE_PICKER && !dates) {
      setDates(defaultRange);
    }
    if (!featureFlags.PORTAL_NEW_REPORTS_DATE_PICKER && !dates) {
      setDates(defaultDates);
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isFetching } = useRevenueByTypeReport({
    api,
    dateFrom: dates?.startDate,
    dateTo: dates?.endDate,
    tz: club.timezone,
    limit: pageSize,
    page: currentPage,
    revenueType: RevenueType.AccountsReceivable,
  });

  const downloadCsvMutation = useRevenueByTypeReportCsv({
    api,
    tz: club.timezone,
  });

  const columnHelper = createColumnHelper<TransactionReportItemDTO>();
  const columnsDefinition = [
    columnHelper.accessor("invoiceDate", {
      cell: (info) => (
        <div className="text-gray-600">
          {moment(info.getValue(), PARAMETER_DATE_FORMAT_WITHOUT_TZ).format(
            properties.dateFormat,
          )}
        </div>
      ),
      header: () => <div>Invoice Date</div>,
      enableSorting: false,
    }),
    columnHelper.accessor("name", {
      cell: (info) => {
        return <div className="text-gray-600">{info.getValue()}</div>;
      },
      header: "Name",
      enableSorting: false,
    }),
    columnHelper.accessor("amount", {
      cell: (info) => (
        <div className="text-gray-600">
          {formatCurrency(info.getValue(), club.defaultCurrency)}
        </div>
      ),
      header: "Purchase Date",
      footer: () =>
        `Total: ${
          data?.total !== undefined
            ? formatCurrency(data.total, club.defaultCurrency)
            : ""
        }`,
      enableSorting: false,
    }),
  ];

  const handleDateRangeChange = useCallback(
    (newDate: any) => {
      if (newDate) {
        setDates({
          startDate: moment(newDate.startDate, DATE_FORMAT).format(
            PARAMETER_DATE_FORMAT_WITHOUT_TZ,
          ),
          endDate: moment(newDate.endDate, DATE_FORMAT)
            .endOf("day")
            .format(PARAMETER_DATE_FORMAT_WITHOUT_TZ),
        });
      }
    },
    [setDates],
  );

  const onDateSelectionApply = useCallback(
    (
      newDate: DateRange | { startDate: string; endDate: string } | undefined,
    ) => {
      const formatted = formatDateRange(newDate);
      setDates(formatted);
    },
    [setDates],
  );

  const onClose = useCallback(() => {
    history.push({
      pathname: searchParams["backRoute"] as string,
      search: qs.stringify({
        dates: {
          startDate: DateTime.fromISO(dates.startDate).toFormat(
            DATE_FORMAT_LUXON,
          ),
          endDate: DateTime.fromISO(dates.endDate).toFormat(DATE_FORMAT_LUXON),
        },
      }),
    });
  }, [dates, history, searchParams]);

  //Will be removed when new date picker is fully rolled out
  const onCloseOld = useCallback(() => {
    history.push({
      pathname: searchParams["backRoute"] as string,
      search: qs.stringify({
        dates: {
          startDate: searchParams["startDate"],
          endDate: searchParams["endDate"],
        },
      }),
    });
  }, [history, searchParams]);

  return (
    <Report
      title="Accounts Receivable"
      description="List of all invoices that are past due within the specified date range."
      onDownloadClick={async () => {
        const data = await downloadCsvMutation.mutateAsync({
          revenueType: RevenueType.AccountsReceivable,
          dateFrom: dates?.startDate,
          dateTo: dates?.endDate,
        });
        downloadCsv(data, `AccountsReceivable.csv`);
      }}
      onDateSelectionApply={onDateSelectionApply}
      handleDateRangeChange={handleDateRangeChange}
      dateRange={
        dates?.startDate && dates?.endDate
          ? featureFlags.PORTAL_NEW_REPORTS_DATE_PICKER
            ? {
                from: new Date(dates.startDate),
                to: new Date(dates.endDate),
              }
            : {
                startDate: moment(
                  dates.startDate,
                  PARAMETER_DATE_FORMAT_WITHOUT_TZ,
                ).format(DATE_FORMAT),
                endDate: moment(dates.endDate, PARAMETER_DATE_FORMAT_WITHOUT_TZ)
                  .endOf("day")
                  .format(DATE_FORMAT),
              }
          : undefined
      }
      close={featureFlags.PORTAL_NEW_REPORTS_DATE_PICKER ? onClose : onCloseOld}
      table={
        <>
          <Table
            data={data?.data.content ?? []}
            columns={columnsDefinition}
            pageCount={data?.data.totalPages}
            pageIndex={data?.data.number}
            onSortingChange={setCurrentSort}
            sort={currentSort}
            isFetching={isFetching}
            pageSize={pageSize}
            rowClassName="h-14"
          />
          <div className="border-t border-gray-300">
            <ReportPagination
              pageCount={data?.data.totalPages as number}
              currentPage={data?.data.number as number}
              onPageChange={(newPage) => {
                setCurrentPage(newPage);
              }}
              pageSize={pageSize}
              setPageSize={(newValue) => {
                setPageSize(newValue);
                setCurrentPage(0);
              }}
            />
          </div>
        </>
      }
    />
  );
}
